import { forwardRef, type ButtonHTMLAttributes, type MouseEvent, type ReactNode } from 'react';

import { cn } from '../../utils';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** @default 'primary' */
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger' | 'dark';
  size?: 'xs' | 'sm' | 'md' | 'lg';
  loading?: boolean;
  fullWidth?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  /**
   * Con `disabled`, deja el botón enfocable: usa `aria-disabled` en vez del
   * atributo nativo y bloquea el clic. Para botones deshabilitados que explican
   * el motivo con un `Tooltip` — un `disabled` nativo no recibe foco y el
   * tooltip queda inalcanzable por teclado (WCAG 2.1.1).
   */
  focusableWhenDisabled?: boolean;
}

const variantStyles = {
  primary:
    'bg-primary-500 text-tkf-fg-on-primary font-bold hover:bg-primary-600 focus:ring-primary-500 disabled:bg-text-alpha-20 disabled:text-tkf-fg aria-disabled:bg-text-alpha-20 aria-disabled:text-tkf-fg',
  secondary:
    'border-0.5 border-tkf-btn-secondary-border bg-tkf-btn-secondary-bg text-tkf-btn-secondary-fg font-bold hover:bg-tkf-btn-secondary-bg-hover focus:ring-primary-500 disabled:bg-tkf-surface-muted aria-disabled:bg-tkf-surface-muted',
  outline:
    'border-0.5 border-tkf-btn-outline-border bg-tkf-btn-outline-bg text-tkf-btn-outline-fg font-bold hover:bg-tkf-btn-outline-bg-hover focus:ring-primary-500 disabled:bg-tkf-surface-muted aria-disabled:bg-tkf-surface-muted',
  ghost:
    'bg-transparent text-tkf-fg font-medium hover:bg-text-alpha-5 focus:ring-primary-500 aria-disabled:bg-transparent',
  danger:
    'bg-error-500 !text-white font-bold hover:bg-error-600 focus:ring-tkf-line-error disabled:bg-error-300 disabled:!text-white aria-disabled:bg-error-300',
  dark: 'bg-black !text-white font-bold hover:bg-neutral-800 focus:ring-neutral-700 disabled:bg-neutral-600 disabled:!text-white/70 aria-disabled:bg-neutral-600 aria-disabled:!text-white/70',
};

const sizeStyles = {
  xs: 'h-7 rounded-lg px-2.5 text-xs',
  sm: 'h-8 rounded-lg px-3 text-label uppercase tracking-tight-tankef',
  md: 'h-10 rounded-lg px-6 text-label uppercase tracking-tight-tankef',
  lg: 'h-12 rounded-lg px-6 text-label uppercase tracking-tight-tankef',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      loading = false,
      fullWidth = false,
      leftIcon,
      rightIcon,
      disabled,
      focusableWhenDisabled = false,
      className,
      children,
      onClick,
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled || loading;
    const softDisabled = isDisabled && focusableWhenDisabled;

    const handleClick = softDisabled
      ? (e: MouseEvent<HTMLButtonElement>) => e.preventDefault()
      : onClick;

    return (
      <button
        ref={ref}
        className={cn(
          'inline-flex items-center justify-center transition-colors duration-200',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
          'disabled:cursor-not-allowed disabled:opacity-50',
          'aria-disabled:cursor-not-allowed aria-disabled:opacity-50',
          variantStyles[variant],
          sizeStyles[size],
          fullWidth && 'w-full',
          className
        )}
        {...props}
        disabled={softDisabled ? undefined : isDisabled}
        aria-disabled={softDisabled ? true : props['aria-disabled']}
        onClick={handleClick}
      >
        {loading ? (
          <svg className="mr-2 h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        ) : leftIcon ? (
          <span className="mr-2">{leftIcon}</span>
        ) : null}
        {children}
        {rightIcon && !loading && <span className="ml-2">{rightIcon}</span>}
      </button>
    );
  }
);

Button.displayName = 'Button';
